import theme from '@liveauctioneers/caterwaul-components/lib/theme/theme';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { BodyPrimary, BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import React, { useState } from 'react';
import styled from 'styled-components';

import DescriptionLink from '@/components/GridItemCard/Card/components/DescriptionLink/DescriptionLink';
import ItemCardAuctionName from '@/components/GridItemCard/Card/components/ItemCardAuctionName/ItemCardAuctionName';
import ItemCardBadge from '@/components/GridItemCard/Card/components/ItemCardBadge/ItemCardBadge';
import ItemCardImage from '@/components/GridItemCard/Card/components/ItemCardImage/ItemCardImage';
import ItemCardPriceRow from '@/components/GridItemCard/Card/components/ItemCardPriceRow/ItemCardPriceRow';
import ItemCardSaveIcon from '@/components/GridItemCard/Card/components/ItemCardSaveIcon/ItemCardSaveIcon';
import ItemCardShortDate from '@/components/GridItemCard/Card/components/ItemCardShortDate/ItemCardShortDate';
import ItemCardTitle from '@/components/GridItemCard/Card/components/ItemCardTitle/ItemCardTitle';
import { ItemCardComponent, ItemCardInfo } from '@/components/GridItemCard/types/types';
import useIsWiderThan from '@/hooks/useIsWiderThan';
import { useAppSelector } from '@/redux/hooks';
import {
    getItemSummaryBuyNowStatus,
    getItemSummaryIsLocked,
    getItemSummaryIsPassed,
    getItemSummaryIsSold,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { getLiveItemStatusOpen } from '@/redux/modules/liveItemStatus';
import { BuyNowStatus } from '@/types/item/ItemSummary';

const LINE_COUNT_FOR_TITLE = 1;

const SalesHistoryCard: ItemCardComponent = ({ className, dimension, itemId }) => {
    const isLotOpen = useAppSelector((state) => getLiveItemStatusOpen(state, itemId));
    const buyNowStatus = useAppSelector((state) => getItemSummaryBuyNowStatus(state, itemId));
    const isLocked = useAppSelector((state) => getItemSummaryIsLocked(state, itemId));
    const isSold = useAppSelector((state) => getItemSummaryIsSold(state, itemId));
    const isPassed = useAppSelector((state) => getItemSummaryIsPassed(state, itemId));
    const [hoveringLink, setHoveringLink] = useState(false);
    const isMobile = !useIsWiderThan(theme.breakpoints.tabletNarrowWidthNum);

    const isBuyNow = buyNowStatus === BuyNowStatus.Available;

    const isPending = isLocked && !isPassed && !isSold && !isLotOpen;
    const auctionDateInfo = isBuyNow ? (
        <BodySecondary color="secondary">
            <FormattedMessage id="buy_now" />
        </BodySecondary>
    ) : (
        <StyledDateContainer>
            {!isMobile && (
                <StyledPrimary color="secondary">
                    <FormattedMessage id="sold" />
                </StyledPrimary>
            )}
            <StyledItemCardShortDate
                itemId={itemId}
                showOnlyYear={isPassed || isPending || isMobile}
                usePrimary
            />
        </StyledDateContainer>
    );

    const seeSoldPriceElement = (
        <PlacedItemPriceRow
            isHovered={hoveringLink}
            itemId={itemId}
            onMouseEnter={() => {
                setHoveringLink(true);
            }}
            onMouseLeave={() => {
                setHoveringLink(false);
            }}
        />
    );
    return (
        <SalesHistoryCardGrid
            $dimension={dimension}
            className={className}
            data-testid="upcoming-sales-price-results-card-grid"
        >
            <StyledImageContainer
                data-testid="image-container"
                onMouseEnter={() => {
                    setHoveringLink(true);
                }}
                onMouseLeave={() => {
                    setHoveringLink(false);
                }}
            >
                <ItemCardBadge
                    className="left-xs top-xs"
                    itemId={itemId}
                    pushSoldBadgeToLeftCorner={isMobile}
                    savedItemsCard
                />
                <StyledFavoriteButton>
                    <ItemCardSaveIcon itemId={itemId} />
                </StyledFavoriteButton>
                <PlacedItemCardImage
                    dimension={dimension}
                    itemId={itemId}
                    showBidStatus
                />
            </StyledImageContainer>
            <PlacedDetailsForNewMobileGrid>
                <PlacedTitleAndPrice>
                    <StyledItemCardTitle
                        isHovered={hoveringLink}
                        itemId={itemId}
                        lineCount={LINE_COUNT_FOR_TITLE}
                        onMouseEnter={() => {
                            setHoveringLink(true);
                        }}
                        onMouseLeave={() => {
                            setHoveringLink(false);
                        }}
                        testid="itemCardTitle"
                    />
                    {!isMobile && seeSoldPriceElement}
                </PlacedTitleAndPrice>
                <PlacedItemInfo>
                    <StyledDateInfo>
                        {auctionDateInfo}
                        {!isMobile && (
                            <ItemCardAuctionName
                                itemId={itemId}
                                showFullAuctionName={!isMobile}
                                usePrimary
                            />
                        )}
                    </StyledDateInfo>
                </PlacedItemInfo>
                <PlacedItemDescription>
                    <DescriptionLink
                        increaseLineHeight
                        increaseTextSize
                        isHovered={hoveringLink}
                        itemId={itemId}
                        lineCount={2}
                        onMouseEnter={() => {
                            setHoveringLink(true);
                        }}
                        onMouseLeave={() => {
                            setHoveringLink(false);
                        }}
                    />
                </PlacedItemDescription>
                {isMobile && seeSoldPriceElement}
            </PlacedDetailsForNewMobileGrid>
        </SalesHistoryCardGrid>
    );
};

const info: ItemCardInfo = {
    dimensions: { desktop: 180, mobile: 116 },
    ItemCardComponent: SalesHistoryCard,
};

export default info;

const StyledDateContainer = styled.div`
    display: flex;
    flex-grow: 1;
`;

const StyledPrimary = styled(BodyPrimary)`
    margin-right: 6px;
`;

const PlacedItemCardImage = styled(ItemCardImage)`
    grid-area: itemimage;
`;

const PlacedDetailsForNewMobileGrid = styled.div`
    display: grid;
    overflow: hidden;
    grid-area: alldetailsformobilelayout;
    grid-template-areas: '.' 'title' '.' 'iteminfo' '.' 'itemdescription';
    grid-template-rows: 2px 24px 8px min-content 8px;
    @media (max-width: ${theme.breakpoints.tabletNarrowWidth}) {
        grid-template-areas: '.' 'iteminfo' '.' 'title' '.' 'itemdescription' '.' 'seesoldprice';
        grid-template-rows: 2px min-content 4px min-content 4px min-content 4px;
    }
`;

const StyledFavoriteButton = styled.span`
    position: absolute;
    top: 8px;
    right: 8px;
    z-index: 2;
    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        top: 6px;
        right: 6px;
    }
`;

const PlacedItemInfo = styled.div`
    grid-area: iteminfo;
`;

const StyledDateInfo = styled.span`
    display: flex;
    line-height: 18px;
`;

const PlacedTitleAndPrice = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    grid-area: title;
`;

const PlacedItemDescription = styled.div`
    max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth};
    grid-area: itemdescription;
`;

const StyledImageContainer = styled.div`
    position: relative;
`;

const SalesHistoryCardGrid = styled.section<{
    $dimension: number;
}>`
    display: grid;
    grid-template-columns: ${({ $dimension }) => $dimension}px;
    margin: 0 auto;
    width: 100%;
    grid-column-gap: 32px;
    grid-template-rows: ${({ $dimension }) => $dimension}px min-content;
    grid-template-areas:
        'itemimage alldetailsformobilelayout'
        '. alldetailsformobilelayout';

    @media (max-width: ${theme.breakpoints.tabletNarrowWidth}) {
        padding: 16px 0;
        grid-column-gap: 16px;
        border-bottom: 1px solid ${({ theme }) => theme.colors.grey400};
    }
`;

const StyledItemCardShortDate = styled(ItemCardShortDate)`
    padding-right: 16px;
    border-right: 1px solid ${theme.colors.grey400};
    margin-right: 16px;

    @media (max-width: ${theme.breakpoints.tabletNarrowWidth}) {
        padding-right: unset;
        border-right: none;
        margin-right: unset;
    }
`;

const PlacedItemPriceRow = styled(ItemCardPriceRow)`
    grid-area: seesoldprice;
    min-width: fit-content;
`;

const StyledItemCardTitle = styled(ItemCardTitle)`
    max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth};
    @media (max-width: ${theme.breakpoints.tabletWidth}) {
        margin-right: 32px;
    }
`;
